import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Button, FormGroup, Label, Row, Col, Spinner } from 'reactstrap';
import Select from 'react-select';
import { useAuth } from '../../Provider/AuthProvider';
import { Product } from './Interfaces';

interface CreateReferenceModalProps {
    isOpen: boolean;
    toggle: () => void;
    initialRef: string;
    product?: Product | null;
    onCreate: (data: {
        ref: string;
        originalRef: string;
        name: string;
        price: string;
        brand: string;
        categoryApa: string;
        categoryGolda: string;
    }) => void;
}

interface DropdownOption {
    cats: any;
    subcategories: any;
    id: string;
    name: string;
}

interface Brand {
    rowid: string;
    name: string;
    brandid: number;
}

interface BrandsResponse {
    success: boolean;
    message: string;
    retour: Brand[];
}

interface ArticleAttribute {
    attrName: string;
    attrValue: string;
    attrBlockNo?: number;
    attrId?: number;
    attrIsConditional?: boolean;
    attrIsInterval?: boolean;
    attrIsLinked?: boolean;
    attrShortName?: string;
    attrType?: string;
    attrValueId?: number;
    attrUnit?: string;
}

interface TecDocResponse {
    success: boolean;
    retour: Array<{
        data: {
            articleName: string;
            articleNo: string;
            brandName: string;
            brandNo: number;
        };
        info?: {
            articleAttributes?: {
                array: ArticleAttribute[];
            };
        };
    }>;
}

interface GoldaCategory {
    name: string;
    id: string;
    cats: Array<{
        name: string;
        id: string;
    }>;
}

interface GoldaResponse {
    success: boolean;
    message: string;
    retour: GoldaCategory[];
}

interface ApaCategory {
    rowid: string;
    label: string;
    fk_parent: string;
}

interface ApaResponse {
    success: boolean;
    message: string;
    data: ApaCategory[];
    count: number;
}

const CreateReferenceModal: React.FC<CreateReferenceModalProps> = ({
    isOpen,
    toggle,
    initialRef,
    product,
    onCreate
}) => {
    const [ref, setRef] = useState(initialRef);
    const [originalRef, setOriginalRef] = useState('');
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [categoryApa, setCategoryApa] = useState('');
    const [categoryGolda, setCategoryGolda] = useState('');
    const [brands, setBrands] = useState<Brand[]>([]);
    const [categoriesApa, setCategoriesApa] = useState<DropdownOption[]>([]);
    const [categoriesGolda, setCategoriesGolda] = useState<DropdownOption[]>([]);
    const [isLoading, setIsLoading] = useState({
        brands: false,
        categoriesApa: false,
        categoriesGolda: false,
        tecdoc: false
    });
    const [showAdditionalFields, setShowAdditionalFields] = useState(false);
    const { user } = useAuth();

    const categoryStyles = {
        mainCategory: {
            fontWeight: 'bold',
            color: '#2e7d32',
            backgroundColor: '#e8f5e9',
            padding: '5px 10px',
            margin: '2px 0',
            borderRadius: '4px'
        },
        subCategory: {
            color: '#1b5e20',
            paddingLeft: '20px',
            fontStyle: 'italic',
            margin: '2px 0'
        },
        select: {
            border: '1px solid #2e7d32',
            borderRadius: '4px',
            padding: '8px'
        }
    };

    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            height: '38px',
            minHeight: '38px',
            fontSize: '0.875rem', // Reduced to match reactstrap input
            padding: '0.375rem 0.75rem',
            border: '1px solid #ced4da',
            borderRadius: '0.25rem',
            lineHeight: '1.5',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center', // Center text vertically
            '&:hover': {
                borderColor: state.isDisabled ? '#ced4da' : '#2e7d32',
            },
            backgroundColor: state.isDisabled ? '#e9ecef' : '#fff',
            opacity: state.isDisabled ? 0.65 : 1,
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            fontSize: '0.875rem', // Reduced font size for options
            ...(state.data.isMainCategory ? categoryStyles.mainCategory : categoryStyles.subCategory),
            ...(state.data.isDisabled ? { color: '#999', cursor: 'not-allowed' } : {}),
        }),
        singleValue: (provided: any) => ({
            ...provided,
            fontSize: '0.875rem', // Reduced to match reactstrap
            lineHeight: '1.5',
            margin: '0',
            position: 'static',
            transform: 'none',
        }),
        input: (provided: any) => ({
            ...provided,
            fontSize: '0.875rem', // Reduced to match reactstrap
            lineHeight: '1.5',
            padding: '0',
            margin: '0',
        }),
        placeholder: (provided: any) => ({
            ...provided,
            fontSize: '0.875rem', // Reduced to match reactstrap
            lineHeight: '1.5',
            color: '#6c757d',
            margin: '0',
        }),
    };

    useEffect(() => {
        if (isOpen) {
            setRef(product?.crm?.ref || initialRef);
            setOriginalRef('');
            setName('');
            setPrice('');
            setSelectedBrand(null);
            setCategoryApa('');
            setCategoryGolda('');
            setShowAdditionalFields(false);
        }
    }, [isOpen, initialRef, product]);

    useEffect(() => {
        if (isOpen) {
            fetchBrands();
        }
    }, [isOpen]);

    const fetchBrands = async () => {
        try {
            setIsLoading(prev => ({ ...prev, brands: true }));

            const brandsResponse: any = await axios.post<BrandsResponse>(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "brands",
                    entity: user.entity
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (brandsResponse.success && brandsResponse.retour) {
                setBrands(brandsResponse.retour);
            } else {
                console.error('Failed to fetch brands:', brandsResponse.message);
            }

            setIsLoading(prev => ({ ...prev, brands: false }));
        } catch (error) {
            console.error('Error fetching brands:', error);
            setIsLoading(prev => ({ ...prev, brands: false }));
        }
    };

    const fetchTecDocInfo = async () => {

        if (!ref.trim() || !selectedBrand) {
            alert('Veuillez entrer une référence et sélectionner une marque');
            return;
        }

        try {
            setIsLoading(prev => ({ ...prev, tecdoc: true }));

            const response: any = await axios.post<TecDocResponse>(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "tdinfo",
                    ref: ref.trim(),
                    brandid: selectedBrand.brandid,
                    entity: user.entity,
                    res: "all"
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (response.success && response.retour?.length > 0) {
                const tecDocData = response.retour[0];
                setName(tecDocData.data.articleName || '');
                setOriginalRef(tecDocData.data.articleNo || '');

                if (tecDocData.info?.articleAttributes?.array) {
                    const attributes = tecDocData.info.articleAttributes.array;

                    const apaCategory = attributes.find((attr: ArticleAttribute) =>
                        attr.attrName.toLowerCase().includes('apa') ||
                        attr.attrName.toLowerCase().includes('category')
                    );
                    if (apaCategory) {
                        setCategoryApa(apaCategory.attrValue);
                    }

                    const goldaCategory = attributes.find((attr: ArticleAttribute) =>
                        attr.attrName.toLowerCase().includes('golda') ||
                        attr.attrName.toLowerCase().includes('category')
                    );
                    if (goldaCategory) {
                        setCategoryGolda(goldaCategory.attrValue);
                    }
                }

                await fetchCategories();
                setShowAdditionalFields(true);
            } else {
                alert('Aucune information TecDoc trouvée pour cette référence et marque');
            }
        } catch (error) {
            console.error('Error fetching TecDoc info:', error);
            alert('Erreur lors de la récupération des informations TecDoc');
        } finally {
            setIsLoading(prev => ({ ...prev, tecdoc: false }));
        }
    };

    const fetchCategories = async () => {
        try {
            setIsLoading(prev => ({ ...prev, categoriesApa: true, categoriesGolda: true }));

            const goldaResponse: any = await axios.post<GoldaResponse>(
                "https://api.apapiecesauto.fr/apiV2/",
                { action: "catgolda", entity: user.entity },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            if (goldaResponse.retour) {
                setCategoriesGolda(goldaResponse.retour);
            }

            const apaResponse: any = await axios.post<ApaResponse>(
                "https://api.apapiecesauto.fr/apiV2/",
                { action: "localcategories", entity: user.entity },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            if (apaResponse.data) {
                const mainCategories = apaResponse.data
                    .filter((cat: ApaCategory) => cat.fk_parent === "0")
                    .map((cat: ApaCategory) => ({
                        id: cat.rowid,
                        name: cat.label,
                        subcategories: apaResponse.data
                            .filter((subCat: ApaCategory) => subCat.fk_parent === cat.rowid)
                            .map((subCat: ApaCategory) => ({
                                id: subCat.rowid,
                                name: subCat.label
                            }))
                    }));
                setCategoriesApa(mainCategories);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setIsLoading(prev => ({
                ...prev,
                categoriesApa: false,
                categoriesGolda: false
            }));
        }
    };

    const handleSubmit = () => {
        const finalData = {
            ref,
            originalRef,
            name,
            price,
            brand: selectedBrand?.rowid || '',
            categoryApa,
            categoryGolda
        };

        onCreate(finalData);
        toggle();

        console.table(finalData);
    };

    const brandOptions = [
        { value: '', label: 'Sélectionner une marque' },
        ...brands.map((brand) => ({
            value: brand.rowid,
            label: brand.name,
            brandData: brand
        }))
    ];

    const apaCategoryOptions = [
        { value: '', label: 'Sélectionner une catégorie', isMainCategory: false, isDisabled: false },
        ...categoriesApa.flatMap((category) => {
            const hasSubcategories = category.subcategories && category.subcategories.length > 0;
            return [
                {
                    value: category.id,
                    label: category.name,
                    isMainCategory: true,
                    isDisabled: hasSubcategories // Disable main category if it has subcategories
                },
                ...(category.subcategories?.map((subCategory: any) => ({
                    value: subCategory.id,
                    label: subCategory.name,
                    isMainCategory: false,
                    isDisabled: false // Subcategories are always selectable
                })) || [])
            ];
        })
    ];

    const goldaCategoryOptions = [
        { value: '', label: 'Sélectionner une catégorie', isMainCategory: false, isDisabled: false },
        ...categoriesGolda.flatMap((category) => {
            const hasSubcategories = category.cats && category.cats.length > 0;
            return [
                {
                    value: category.id,
                    label: category.name,
                    isMainCategory: true,
                    isDisabled: hasSubcategories // Disable main category if it has subcategories
                },
                ...(category.cats?.map((subCategory: any) => ({
                    value: subCategory.id,
                    label: subCategory.name,
                    isMainCategory: false,
                    isDisabled: false // Subcategories are always selectable
                })) || [])
            ];
        })
    ];

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>Créer une nouvelle référence</ModalHeader>
            <ModalBody>
                <Row className="g-3">
                    <Col md={5}>
                        <FormGroup>
                            <Label>Référence</Label>
                            <Input
                                type="text"
                                value={ref}
                                onChange={(e) => setRef(e.target.value)}
                                placeholder="Référence"
                            />
                        </FormGroup>
                    </Col>

                    <Col md={5}>
                        <FormGroup>
                            <Label>Marque</Label>
                            {isLoading.brands ? (
                                <Spinner size="sm" />
                            ) : (
                                <Select
                                    options={brandOptions}
                                    value={brandOptions.find((option) => option.value === selectedBrand?.rowid)}
                                    onChange={(selectedOption : any) => {
                                        setSelectedBrand(selectedOption?.brandData || null);
                                    }}
                                    isSearchable
                                    placeholder="Sélectionner une marque"
                                    classNamePrefix="react-select"
                                    styles={selectStyles}
                                    isDisabled={isLoading.brands}
                                />
                            )}
                        </FormGroup>
                    </Col>

                    <Col md={2} className="d-flex align-items-end">
                        <Button
                            color="primary"
                            onClick={fetchTecDocInfo}
                            disabled={!ref.trim() || !selectedBrand || isLoading.tecdoc}
                            className="w-100"
                            style={{ height: '38px', marginBottom: '16px' }}
                        >
                            {isLoading.tecdoc ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <i className="ri ri-search-line me-2" />Tecdoc
                                </>
                            )}
                        </Button>
                    </Col>
                </Row>

                {showAdditionalFields && (
                    <Row className="g-3 mt-3">
                        <Col md={6}>
                            <FormGroup>
                                <Label>Réf. d'origine</Label>
                                <Input
                                    type="text"
                                    value={""}
                                    onChange={(e) => setOriginalRef(e.target.value)}
                                    placeholder="Référence d'origine"
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup>
                                <Label>Nom</Label>
                                <Input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Nom du produit"
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup>
                                <Label>Prix de vente</Label>
                                <Input
                                    type="number"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    placeholder="Prix TTC"
                                    step="0.01"
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup>
                                <Label>Catégorie APA</Label>
                                {isLoading.categoriesApa ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <Select
                                        options={apaCategoryOptions}
                                        value={apaCategoryOptions.find((option) => option.value === categoryApa)}
                                        onChange={(selectedOption : any) => setCategoryApa(selectedOption ? selectedOption.value : '')}
                                        isSearchable
                                        placeholder="Sélectionner une catégorie"
                                        classNamePrefix="react-select"
                                        styles={selectStyles}
                                        isDisabled={isLoading.categoriesApa}
                                            isOptionDisabled={(option: any ) => option.isDisabled}
                                    />
                                )}
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup>
                                <Label>Catégorie Golda</Label>
                                {isLoading.categoriesGolda ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <Select
                                        options={goldaCategoryOptions}
                                        value={goldaCategoryOptions.find((option) => option.value === categoryGolda)}
                                            onChange={(selectedOption: any) => setCategoryGolda(selectedOption ? selectedOption.value : '')}
                                        isSearchable
                                        placeholder="Sélectionner une catégorie"
                                        classNamePrefix="react-select"
                                        styles={selectStyles}
                                        isDisabled={isLoading.categoriesGolda}
                                            isOptionDisabled={(option: any) => option.isDisabled}
                                    />
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Annuler
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!ref.trim() || !name.trim() || !selectedBrand}
                >
                    Créer Fiche
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CreateReferenceModal;